import React, { useContext, useEffect, useState } from "react";
import Tippy from "@tippyjs/react";
import { useTranslation } from "react-i18next";

import Paper from "@/components/paper/Paper";
import EmployeeSiteBlock from "@/app/pages/companyProfile/overview/EmployeeSiteBlock";
import DrsAndAAASeal from "@/components/DrsAndAAASeal";
import { checkSupression } from "@/utils/index";
import { properties } from "@/testData/companyProfile";
import renderReliability from "@/components/tippy/Tippy";
import getRevenueReliability from "@/app/pages/companyProfile/helper";
import { AppContext, ContextType } from "@/shared/context";
import EsgContainer from "@/app/pages/companyProfile/overview/EsgContainer";
import { startAfterAPIresponse } from "@/app/pages/companyProfile/overview/Helper";
import { usePageURL } from "@/shared/customHooks";
import { isEmpty } from "underscore";
import { track_event } from "@/shared/FunctionCore";

const keyNameList = {};

// I REQUEST NOT TO REMOVE ANY COMMENTED CODE, AS THOSE WILL BE USED LATER ON

const Overview = ({ props, countryCode }): JSX.Element => {
  const { isUnitedStates, isEuBd } = useContext(AppContext) as ContextType;
  const { t } = useTranslation();
  const hostUrl = window.location.href;
  const industryKey = isEuBd ? "euIndustries" : "industries";
  const [showMoreIndustries, toggleMoreIndustries] = useState(false);
  const { dunsNumber } = usePageURL();
  const { encryptedDuns, esgContactUsHrefUrl, esgIndustryAverage, esgRank, esgRankingHrefUrl, magentoCartAddDomain, magentoCartAddEndpoint, magentoCartDisplayName, magentoCartServletPath, primaryName} = props;
  const esgData = { encryptedDuns, esgContactUsHrefUrl, esgIndustryAverage, esgRank, esgRankingHrefUrl, magentoCartAddDomain, magentoCartAddEndpoint, magentoCartDisplayName, magentoCartServletPath, primaryName};

  useEffect(() => {
    startAfterAPIresponse();
  }, []);

  // https://stackoverflow.com/questions/6497992/removing-img-border/31418887#31418887
  // $(".overview-popular-search a img").attr("src", transparentGifAssetInline);

  // Print friendly view
  const print = (ev) => {
    ev.preventDefault();
    window.print()
  }


  const buttonAction = () => {
    let redirection = "";
    if (countryCode == "de" || countryCode == "at") {
      redirection = `${t(
        "companyProfile.financial.getDandBCreditLink"
      )}${dunsNumber}`;
    } else if(countryCode === "us") {
      redirection = `${t("companyProfile.overview.revenueBtnRedirectLink")}`;
    } else {
      redirection = `${t("companyProfile.financial.getDandBCreditLink")}`;
    }
    window.location.href = redirection;
  };
  
  return (
    <>
      <a className="anchor" data-name="overview-anchor" href="/overview">
        <span className="display-hidden">Overview</span>
      </a>
      <Paper title={t("companyProfile.overview.overviewLocalized")}>
        <div className="row">
          <div className="col-md-6 company-profile-overview-starting-margin">
            {props.tradeStyleName && (
              <div className="margin-between-rows">
                <span className="company_data_point" name="company_name">
                  <label>{properties.business_name_label}:&nbsp;</label>
                  <span
                    data-tracking-name="{properties.business_name_label}"
                    className="avenir-light"
                  >
                    {props.tradeStyleName}
                  </span>
                </span>
              </div>
            )}

            {isUnitedStates && props.companyShortSummaryTest && (
              <div className="margin-between-rows">
                <span className="company_data_point" name="company_description">
                  <label>{"Company Description"}:&nbsp;</label>
                  <span
                    data-tracking-name={"Company Description"}
                    className="avenir-light"
                  >
                    {props.companyShortSummaryTest.replace(/[\t\s]/g, '') === '?' ?
                      <button
                        type="button"
                        className="border-0"
                      >
                        {checkSupression(props.companyShortSummaryTest)}
                      </button> : <span>{props.companyShortSummaryTest}</span>}
                  </span>
                  {props.showReadMore && (
                    <div className="d-print-none d-inline">
                      <span className="overview-description-read-more">
                        ...
                        <br />
                        <a className="linkColorOverview">{"Read More"}</a>
                        <span className="chevron-down-arrow-blue"></span>
                      </span>
                      <span className="overview-description-read-less overview-view-hide">
                        <span>{props.descriptionReadMore}</span>
                        <br />
                        <a className="linkColorOverview">{"Read Less"}</a>
                        <span className="chevron-up-arrow-blue"></span>
                      </span>
                    </div>
                  )}
                </span>
              </div>
            )}

            {isEuBd && (
              <div className="margin-between-rows">
                <span className="company_data_point d-flex" name="duns_number">
                  <label>
                    {t("companyProfile.overview.duns_nr_label")}:&nbsp;
                  </label>
                  <span
                    data-tracking-name={t(
                      "companyProfile.overview.duns_nr_label"
                    )}
                  >
                    {hostUrl.includes('/de-de/') ? <a target="_blank" href={`/de-de/upik.html`}>
                      <div className="dataMaskingLockIcon" />
                    </a> : checkSupression(props.encryptedDuns)}
                    
                  </span>
                </span>
              </div>
            )}
            {props.registrationNumber && (
              <div className="margin-between-rows">
                <span className="company_data_point" name="reg_number">
                  <label>
                    {t("companyProfile.overview.registration_nr_label")}:&nbsp;
                  </label>
                  <span
                    data-tracking-name={t(
                      "companyProfile.overview.registration_nr_label"
                    )}
                    className="avenir-light"
                  >
                    {props.registrationNumber}
                  </span>
                </span>
              </div>
            )}
            {countryCode === "pl" && (
              <>
                {props.regon && (
                  <div className="margin-between-rows">
                    <span className="company_data_point" name="regon">
                      <label>Regon:&nbsp;</label>
                      <span
                        data-tracking-name={properties.regon}
                        className="avenir-light"
                      >
                        {props.regon}
                      </span>
                    </span>
                  </div>
                )}
                {props.krs && (
                  <div className="margin-between-rows">
                    <span className="company_data_point" name="krs">
                      <label>KRS:&nbsp;</label>
                      <span
                        data-tracking-name={properties.krs}
                        className="avenir-light"
                      >
                        {props.krs}
                      </span>
                    </span>
                  </div>
                )}
              </>
            )}

            {/* {props.companyShortSummaryTest && !properties.isEuBd && (
            <CompanyShortSummaryTest />
          )} */}
            {props.ranks && (
              <div className="margin-between-rows">
                <span className="company_data_point" name="ranks">
                  <label>{properties.ranking_label}:&nbsp;</label>
                  {props.ranks.map((rank) => (
                    <>
                      <span className="avenir-light">
                        <span className="prefix avenir-light">
                          #{properties.rankPrefix}
                        </span>
                        {rank["value"]}&nbsp;
                      </span>
                      <span className="suffix">{properties.rankSuffix}</span>
                      <span className="avenir-light">
                        {rank["description"]}
                      </span>
                    </>
                  ))}
                </span>
              </div>
            )}

            {/* <span className="company_profile_overview_underline_links avenir-light"> */}
            {/* <div class="margin-between-rows">
              <span class="company_data_point" name="key_principal">
                <label>Hauptfunktionsträger:&nbsp;</label>
                  <span>
                    <span class="avenir-light">Michael Tangney</span>
                    <span class="overview_arrow_link">
                      <a class="jump-link avenir-light" href="{properties.seeMoreContactsAnchor}">Mehr Ansprechpersonen</a>
                      <span class="chevron-right-arrow-blue"></span>
                    </span>
                  </span>
                </span>
              </div> */}

            {props.keyPrincipal && (
              <div className="margin-between-rows">
                <span className="company_data_point" name="key_principal">
                  <label>
                    {t("companyProfile.overview.key_principal_label")}:&nbsp;
                  </label>
                  <span>
                    {props.keyPrincipalProfileLink ? (
                      <span className="overview_arrow_link">
                        <a
                          href={props.keyPrincipalProfileLink}
                          className="jump-link avenir-light"
                        >
                          {props?.keyPrincipal}
                        </a>
                        {/* <span className="chevron-right-arrow-blue"></span> */}
                      </span>
                    ) : (
                      <span className="avenir-light">{props.keyPrincipal}</span>
                    )}{" "}
                    <br />
                    <span
                      className="overview_arrow_link d-print-none"
                    // onClick="track_event('see_more_contacts_clicked');"
                    >
                      <a
                        className="jump-link light-text"
                        href="#contact-list"
                      >
                        {t("companyProfile.overview.seeMoreContactsLabel")}
                      </a>
                      <a
                        href="#contact-list"
                        className="chevron-right-arrow-blue cursor-pointer"
                      ></a>
                    </span>
                  </span>
                </span>
              </div>
            )}

            {props[industryKey] && (
              <>
                <div className="margin-between-rows">
                  <span className="company_data_point" name="industry_links">
                    <label>
                      {t("companyProfile.overview.industry_label")}:&nbsp;
                    </label>
                    {isEuBd && (
                      <span>
                        <span className="avenir-light">
                          {props[industryKey]}
                        </span>
                        {/* <sly data-sly-test="{!keyNameList.last}">,&nbsp;</sly> */}
                      </span>
                    )}
                    {isUnitedStates &&
                      Object.entries(props[industryKey]).map(
                        ([label, href], index: number, array) => (
                          <div key={index} className="d-inline">
                            {href && (
                              <a
                                className="company_profile_overview_underline_links"
                                href={`/business-directory/industry-analysis.${href}.html`}
                              >
                                {label}
                              </a>
                            )}
                            {!href && <span>{label}</span>}
                            {index < array.length - 1 && ", "}
                          </div>
                        )
                      )}
                    {isUnitedStates && !isEmpty(props["viewMoreIndustries"]) && (
                      <span>
                        <span className="overview-industries-view-more">
                          <br />
                          <a className="light-text">{"See All Industries"}</a>
                          <span className="chevron-down-arrow-blue"></span>
                        </span>
                        <span className="overview-view-more-industries-list overview-view-hide">
                          {Object.entries(props["viewMoreIndustries"]).map(
                            ([label, href], index, array) => (
                              <>
                                {href && (
                                  <a
                                    className="company_profile_overview_underline_links"
                                    href={`/business-directory/industry-analysis.${href}.html`}
                                  >
                                    {label}
                                  </a>
                                )}
                                {!href && <span>{label}</span>}
                                {index < array.length - 1 && ", "}
                              </>
                            )
                          )}
                        </span>
                        <span className="overview-industries-view-less overview-view-hide">
                          <br />
                          <a className="light-text">{"See Fewer Industries"}</a>
                          <span className="chevron-up-arrow-blue"></span>
                        </span>
                      </span>
                    )}
                    {props.euViewMoreIndustries && isEuBd && (
                      <>
                        {!showMoreIndustries ? (
                          <span className="overview-industries-view-more">
                            <br />
                            {/* eslint-disable-next-line */}
                            <a
                              onClick={() => {
                                toggleMoreIndustries(!showMoreIndustries);
                              }}
                              className="anchor light-text cursor-pointer"
                            >
                              {t(
                                "companyProfile.overview.industries_view_more_label"
                              )}
                            </a>
                            <span
                              onClick={() => {
                                toggleMoreIndustries(!showMoreIndustries);
                              }}
                              className="chevron-down-arrow-blue cursor-pointer"
                            ></span>
                          </span>
                        ) : (
                          <>
                            <span className="overview-view-more-industries-list avenir-light">
                              <span>
                                {/* {!props.euViewMoreIndustries}{props.euViewMoreIndustries} */}
                                {props.euViewMoreIndustries}
                              </span>
                            </span>
                            <span className="overview-industries-view-less">
                              <br />
                              {/* eslint-disable-next-line */}
                              <a
                                onClick={() => {
                                  toggleMoreIndustries(!showMoreIndustries);
                                }}
                                className="anchor light-text cursor-pointer"
                              >
                                {t(
                                  "companyProfile.overview.industries_view_less_label"
                                )}
                              </a>
                              <span
                                onClick={() => {
                                  toggleMoreIndustries(!showMoreIndustries);
                                }}
                                className="chevron-up-arrow-blue cursor-pointer"
                              ></span>
                            </span>
                          </>
                        )}
                      </>
                    )}
                    {/* <sly data-sly-test="${overviewPojo.euViewMoreIndustries}">
                                    <span className='overview-industries-view-more'><br/>
                                        <a>${properties.industries_view_more_label}</a>
                                            <span className="chevron-down-arrow-blue"></span>
                                    </span>
                                    <span className="overview-view-more-industries-list overview-view-hide">
                                        <span data-sly-list.keyName="${overviewPojo.euViewMoreIndustries}">
                                            <span>
                                                <sly data-sly-test="${!keyNameList.last}">,&nbsp;</sly>
                                                <span data-sly-test="${!overviewPojo.euViewMoreIndustries[keyName]}">${keyName}</span>
                                            </span>
                                        </span>
                                    </span>
                                    <span className="overview-industries-view-less overview-view-hide">
                                        <br/><a>${properties.industries_view_less_label}</a>
                                        <span className="chevron-up-arrow-blue"></span>
                                    </span>
                                </sly> */}
                  </span>
                </div>{" "}
              </>
            )}

            {props.otherIndustries && (
              <div className="margin-between-rows">
                <span
                  className="company_data_point"
                  name="other_industries_links"
                >
                  <label>
                    {properties.other_industries_label}&nbsp;
                    {props.parentIndustryName}&nbsp;
                    {properties.other_industries_sector_label}:
                  </label>
                  {Object.entries(props["otherIndustries"]).map(
                    ([label, href], index, array) => (
                      <div key={index} className="d-inline">
                        {href && (
                          <a
                            className="company_profile_overview_underline_links"
                            href={`/business-directory/industry-analysis.${href}.html`}
                          >
                            {label}
                          </a>
                        )}
                        {!href && <span>{label}</span>}
                        {index < array.length - 1 && ", "}
                      </div >
                    )
                  )}
                  {  props.viewMoreOtherIndustries && !isEmpty(props.viewMoreOtherIndustries) && (
                    <span>
                      <span className="overview-other-industries-view-more d-print-none">
                        <br />
                        <a className="linkColorOverview">{'View more'}</a>
                        <span className="chevron-down-arrow-blue"></span>
                      </span>
                      <span className="overview-view-more-other-industries-list overview-view-hide">
                          {Object.entries(props["viewMoreOtherIndustries"]).map(
                            ([label, href], index, array) => (
                              <>
                                {href && (
                                  <a
                                    className="company_profile_overview_underline_links linkColorOverview"
                                    href={`/business-directory/industry-analysis.${href}.html`}
                                  >
                                    {label}
                                  </a>
                                )}
                                {!href && <span>{label}</span>}
                                {index < array.length - 1 && ", "}
                              </>
                            )
                          )}
                        </span>
                        <span className="overview-other-industries-view-less overview-view-hide">
                          <br />
                          <a className="linkColorOverview">{'View fewer'}</a>
                          <span className="chevron-up-arrow-blue"></span>
                        </span>
                    </span>
                  )}
                </span>
              </div>
            )}

            {props.popularIndustries && isUnitedStates && (
              <div className="margin-between-rows d-print-mt-25">
                <div className="company_data_point d-print-flex" name="popular_search_links">
                  <label>{properties.popular_search_label}:&nbsp;</label>
                  {props.popularIndustries.map((popularIndustry,indexs) => {
                    if (popularIndustry.name) {
                      return (
                        <div key={indexs}> 
                          <span
                            className={`overview-popular-search overview-popular-color-theme-${popularIndustry.colorIndex}`}
                          >
                            <a href={`/business-directory/company-information.${popularIndustry.url}.html`}>
                              <span
                                className={`overview-popular-icon-${popularIndustry.colorIndex}`}
                              ></span>
                              <span>{popularIndustry.name}</span>
                            </a>
                          </span>
                        </div>
                      );
                    }
                    return "";
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            className="col-md-6 col-md-offset-1 company-profile-overview-starting-margin"
            id="company_profile_snapshot"
          >
            {/* <!-- Print Friendly View --> */}
              {isUnitedStates && (
                <div className="row margin-between-rows company_snapshot_display" id="print_friendly_button">
                  <div className="col-md-1 company-profile-overview-icon-print-view"></div>
                  <div className="col-md-11">
                    <span
                      className="company_data_point"
                      name="company_print_view"
                      id="company-print-view"
                    >
                      <span className="company_profile_overview_underline_links">
                        <a
                          className="company_profile_overview_print_view"
                          href="#"
                          onClick={(ev) => {print(ev)} }
                        >
                          Printer Friendly View
                        </a>
                      </span>
                    </span>
                  </div>
                </div>
              )}
            {/* <sly data-sly-include="drsSeal.jsp"/> */}

            {/* INCLUDE/CALL DRS COMPONENT FROM JSP */}
            <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0 pl-20px">
              {props.drs && (
                <DrsAndAAASeal
                  isHu={countryCode === "hu"}
                  meritColor={props.meritColor}
                  meritScore={props.meritScore}
                  drsContentLocalized={t("companyProfile.overview.sealText")}
                />
              )}
            </div>

            <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0">
              <div className="col-md-1 company-profile-overview-icon-address"></div>
              <div className="col-md-11">
                <span
                  className="company_data_point"
                  name={t("companyProfile.overview.address_label")}
                >
                  <label>
                    {t("companyProfile.overview.address_label")}:&nbsp;
                  </label>
                  <span>
                    <a
                      data-sly-unwrap={
                        props.partnershipCompany || props.proprietorshipCompany
                      }
                      href={`https://maps.google.com/?q=${props.companyLocation.replace('?', '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="ext-class ext-icon"
                    >
                      {checkSupression(props.companyLocation)}
                    </a>
                    {props.isAddressUnverified && (
                      <Tippy
                        theme="dnbtooltip"
                        content="Information needs to be verified"
                        className="reliability-tool-tip"
                      >
                        <span className="icon unverified-address-icon"></span>
                      </Tippy>
                    )}
                    {props.showSeeOtherLocations && (
                      <span className="overview_arrow_link d-print-none">
                        <a
                          href={properties.seeOtherLocationsAnchor}
                          className="jump-link cursor-pointer"
                        // onClick={track_event('see_all_locations_clicked')}
                        >
                          {properties.seeOtherLocationLabel}
                          <span className="chevron-right-arrow-blue"></span>
                        </a>
                      </span>
                    )}

                    {props.suppressContactData && (
                      <div className="overview-location-subscription">
                        {properties.subscriptionMessage}
                      </div>
                    )}
                  </span>
                  {/* // for EU */}

                  {props.companyDisplayLocation && (
                    <>
                      <br />
                      <span className="avenir-light">
                        {props.companyDisplayLocation}
                      </span>
                    </>
                  )}
                </span>
              </div>
            </div>

            {props.phone && (
              <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0">
                <div className="col-md-1 company-profile-overview-icon-phone"></div>
                <div className="col-md-11">
                  <span className="company_data_point" name="company_phone">
                    <label>
                      {t("companyProfile.overview.business_phone_label")}:&nbsp;
                    </label>
                    <span
                      data-tracking-name={t(
                        "companyProfile.overview.business_phone_label"
                      )}
                    >
                      {checkSupression(props.phone)}
                    </span>
                  </span>
                </div>
              </div>
            )}
            {props.website && (
              <div className="row margin-between-rows company_snapshot_display">
                <div className="col-md-1 company-profile-overview-icon-website"></div>
                <div className="col-md-11">
                  <span className="company_data_point" name="company_website">
                    <label>
                      {t("companyProfile.overview.business_website_label")}
                      :&nbsp;
                    </label>
                    <span className="company_profile_overview_underline_links">
                      <a
                        href={`http://${props.website}`}
                        target="_blank"
                        // rel="nofollow"
                        rel="noreferrer"
                        id="hero-company-link"
                        className="ext-class ext-icon website"
                      >
                        {props.website}
                      </a>
                    </span>
                  </span>
                </div>
              </div>
            )}

            {props.employeesThisSiteValue ||
              props.employeesConsolidatedValue ? (
              <EmployeeSiteBlock
                isEuBd={properties.isEuBd}
                employeesConsolidatedValue={props.employeesConsolidatedValue}
                employeesThisSiteValue={props.employeesThisSiteValue}
                employeesThisSiteReliability={
                  props.employeesThisSiteReliability
                }
                employeesConsolidatedReliability={
                  props.employeesConsolidatedReliability
                }
                countryCode={countryCode}
              />
            ) : (
              <></>
            )}

            {/* /revenue ui for all except EU/ */}
            {props.showRevenueRow && isUnitedStates && (
              <div className="row margin-between-rows company_snapshot_display">
                <div className="col-md-1 company-profile-overview-icon-revenue"></div>
                <div className="col-md-11 flex-shrink-1">
                  {props.formattedRevenue && (
                    <span
                      className="company_data_point"
                      name="revenue_in_us_dollar"
                    >
                      <label>{properties.revenue_label}:&nbsp;</label>
                      <span>{checkSupression(props.formattedRevenue, {buttonLabel: t("companyProfile.overview.revenueBtnLabel"), text: t("companyProfile.overview.revenuePopupDescription"), type: 'revenue', buttonAction: buttonAction})}</span>
                      {props.revenueReliability && (
                        <Tippy
                          theme="dnbtooltip"
                          className="reliability-tag-tool-tip"
                          content={t(`companyProfile.overview.reliabilityDefinitionSales${props.revenueReliability}`)}
                        >
                          <span className="reliability-tag">
                            <span className="reliability-tag-icon"></span>
                            <span className="reliability-tag-label">
                              {props.revenueReliability}
                            </span>
                          </span>
                        </Tippy>
                      )}
                    </span>
                  )}
                  {props.showSalesGrowthRate && (
                    <span
                      className="company_data_point"
                      name="sales_growth_percentage"
                    >
                      <label>{properties.sales_growth_label}:&nbsp;</label>
                      <span>{props.salesGrowthPercent}% </span>
                    </span>
                  )}
                  {props.showNetGrowthRate && (
                    <span
                      className="company_data_point"
                      name="net_income_growth_percentage"
                    >
                      <label>{properties.net_income_growth_label}:&nbsp;</label>
                      <span>
                        {props.netIncomeGrowthPercent}%
                        {/* {properties.growth_percentage_postfix} */}
                      </span>
                    </span>
                  )}
                  {props.assets && (
                    <span className="company_data_point" name="company_assets">
                      <label>{properties.assets_label}:&nbsp;</label>
                      <span>{props.assets} </span>
                    </span>
                  )}
                  {props.fiscalYearEnd && (
                    <span className="company_data_point" name="fiscal_year_end">
                      <label>{properties.fiscal_year_end_label}:&nbsp;</label>
                      <span>{checkSupression(props.fiscalYearEnd)}</span>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* revenue ui for EU */}
            {properties.isEuBd && (
              <>
              {props.revenue && (
                  <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0 ">
                    <div className="col-md-1 company-profile-overview-icon-revenue"></div>
                    <div className="col-md-11">
                      <span
                        className="company_data_point"
                        name="revenue_in_us_dollar"
                      >
                        <label>
                          {t("companyProfile.overview.revenue_label")}:&nbsp;
                        </label>
                        <span>{checkSupression(props.revenue, {buttonLabel: t("companyProfile.overview.revenueBtnLabel"), text: t("companyProfile.overview.revenuePopupDescription"), type: 'revenue', buttonAction: buttonAction})}</span>
                        {getRevenueReliability(
                          props.revenueReliability,
                          t,
                          countryCode
                        )}
                      </span>
                    </div>
              </div>
                )}
                </>
            )}

            <div className="row margin-between-rows company_snapshot_display">
              <div className="col-md-1 company-profile-overview-icon-office-building"></div>
              <div className="col-md-11 d-flex align-items-center">
                <span className="company_data_point" name="year_started">
                  {props.yearStarted && (
                    <>
                      <label>
                        {t("companyProfile.overview.year_started_label")}:&nbsp;
                      </label>
                      <span
                        data-tracking-name={t(
                          "companyProfile.overview.year_started_label"
                        )}
                      >
                        {checkSupression(props.yearStarted)} <br />
                      </span>
                    </>
                  )}
                </span>

                {isUnitedStates && props.incorporatedYear && (
                  <span className="company_data_point" name="year_incorporated">
                    <label>
                      {t("companyProfile.overview.incorporated_label")}:&nbsp;
                    </label>
                    <span
                      data-tracking-name={t(
                        "companyProfile.overview.incorporated_label"
                      )}
                    >
                      {checkSupression(props.incorporatedYear)}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {isUnitedStates && props.stockExchange && (
              <div className="row margin-between-rows company_snapshot_display">
                <div className="col-md-1 company-profile-overview-icon-stockExchange"></div>
                <div className="col-md-11">
                  <span className="company_data_point" name="stock_exchange">
                    <label>{properties.stock_exchange_label}:&nbsp;</label>
                    <span>{props.stockExchange}</span>
                  </span>
                </div>
              </div>
            )}

            {isUnitedStates && <EsgContainer esgData={esgData} />}

            {/* <sly data-sly-test="${!properties.isEuBd}" data-sly-include="esgContainer.jsp"/> */}

            <div className="company-profile-divider"></div>

            <div className="row margin-between-rows company_snapshot_display">
              <div className="col-md-12">
                <span className="company_data_point">
                  <a
                    className="overview_button_cta button  overview_cta_btn overview_button_cta"
                    href={t("companyProfile.overview.staticFreeTrialLink")}
                    target="_self"
                  >
                    {t("companyProfile.overview.staticFreeTrialText")}
                  </a>
                </span>
              </div>
            </div>
            {/* // */}
          </div>
        </div>
      </Paper>
    </>
  );
};

export default Overview;
